import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  HOME_GRID_STORAGE_PREFIX,
  computeHomeSeedPageSize,
} from '@/constant/prefetch-home-size'
import {
  HOME_PERFORMANCE_MARKS,
  markPerformanceStage,
} from '@/utils/firstScreenPerformance'

type RefLike = { current: HTMLElement | null } | null

type Options = {
  /**
   * 卡片高宽比（卡片高 ÷ 卡片宽）。行高 ≈ 列宽 × 此值 + 行间距。
   * 默认 1.6757，对应首页卡片图片区的 `pt-[167.57%]`（移动 / 桌面卡片一致，
   * 标题与数据是 absolute 叠在图片上，不额外占高）。
   */
  itemAspectRatio?: number
  /**
   * 首屏想覆盖的视口倍数，默认 2（备约两屏）。取 2 是为了配合无限滚动的预取：
   * 触底提前量 rootMargin 必须 < (screenBuffer-1)×视口高，备两屏才能把提前量放到
   * 约一屏，让慢接口在用户滚到底前就备好新内容、避免「加载后视口才慢慢下移」。
   */
  screenBuffer?: number
  /** 行数下限兜底（视口异常矮时至少抓这么多行），默认 2 */
  minRows?: number
  /** 行数上限（防止异常超大值导致首页一次拉太多），默认 16 */
  maxRows?: number
  /** 读取真实可视高度的滚动容器 ref；缺省时退化为 window.innerHeight */
  viewportRef?: RefLike
  /** SSR / 未测量且无持久值时的安全 pageSize，需足够大以撑满任意大屏首屏 */
  fallback?: number
  /**
   * 持久化首屏 pageSize 的 key（按端 / 场景区分，如 'home:mobile'）。
   * 传入后会把上次测得的 pageSize 写入 localStorage，下次进入直接用它播种，
   * 避免首屏先用 fallback 大值多拉一页再回收。
   */
  storageKey?: string
}

// STORAGE_PREFIX 复用共享常量（与预取 boot 的种子算法同源，见 constant/prefetch-home-size.ts），
// 避免 key 前缀两边漂移。computeHomeSeedPageSize 也由该共享文件提供，boot 与本 hook 同调一份。
const STORAGE_PREFIX = HOME_GRID_STORAGE_PREFIX

type StoredGrid = { cols: number; rows: number }

const writeStoredGrid = (storageKey: string | undefined, grid: StoredGrid) => {
  if (!storageKey || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      STORAGE_PREFIX + storageKey,
      JSON.stringify(grid)
    )
  } catch {
    // 忽略隐私模式 / 配额异常
  }
}

/**
 * 根据网格容器真实渲染的列宽 / 列数 + 滚动容器可视高度，自适应计算分页 size。
 *
 * 背景：首页 page size 原本写死，与屏幕无关，大屏一屏拉不满、小屏又多拉。
 * 这里挂载后用 ResizeObserver 观察网格容器，从 `grid-template-columns` 解析出
 * 列数与列宽、从 `row-gap` 取行间距。行盒高 = 列宽 × itemAspectRatio（不含间距），
 * 堆叠步距 = 行盒高 + 行间距，用步距反推「铺满首屏需要几行」：
 *   rows     = ceil(viewportHeight × screenBuffer / rowPitch)
 *   pageSize = columns × rows
 * 这样同时适配屏宽（列数 / 列宽）与屏高（行数），无需写死 rows。
 * 另导出 rowHeight = 行盒高（不含间距），给虚拟列表作 itemSize——必须是单行盒子的
 * 真实高度，含间距会偏大、导致 virtua 估算误差在滚动时持续修正总高、视口上移。
 *
 * 关键：返回两个相互解耦的值——
 * - `columns` 仅在真实测量后才从 0 切换，驱动布局 / 虚拟化（必须与 SSR 的 0 一致，
 *   否则会触发 hydration 不匹配）。
 * - `pageSize` 只用于请求 size（不进 SSR DOM），首屏优先用持久化值播种，
 *   测量后再以真实计算为准。
 *
 * SSR / 首次渲染拿不到容器尺寸时：有持久化 pageSize 则用它，否则回退 fallback。
 */
const useResponsivePageSize = (
  gridRef: MutableRefObject<HTMLElement | null>,
  {
    itemAspectRatio = 1.6757,
    screenBuffer = 2,
    minRows = 2,
    maxRows = 16,
    viewportRef,
    fallback = 40,
    storageKey,
  }: Options = {}
) => {
  const [columns, setColumns] = useState(0)
  // 测得的单行盒高（px，不含行间距），透传给虚拟列表作 itemSize 的基数。
  const [rowHeight, setRowHeight] = useState(0)
  const rowHeightRef = useRef(0)
  // 测得的行间距（px）。虚拟化后每行是独立 grid，需由行 div 外边距补回，
  // 并计入 itemSize；这里测出来透传给 VirtualHomeGrid。
  const [rowGap, setRowGap] = useState(0)
  const rowGapRef = useRef(0)
  // 无限滚动预取提前量（按「行」计）：= ceil(视口高 / 行步距)，即「一屏的行数」。
  // 透传给 VirtualHomeGrid 作 endThreshold，让预取提前量恒等于约一屏、随设备视口/
  // 列宽自适应，取代写死的魔数行数。0 表示尚未测量（此时 columns 也为 0，走非虚拟
  // 整块渲染、用不到该值）。
  const [prefetchRows, setPrefetchRows] = useState(0)
  const prefetchRowsRef = useRef(0)
  // 避免列数 / pageSize 抖动触发重复请求
  const columnsRef = useRef(0)
  // 首屏种子 pageSize：用上次持久化的 cols×rows（天然按列对齐，不会出现残行）；
  // 无持久值时回退 fallback。注意不能把列数也种到 columns（会从 0 切非 0 触发
  // hydration 不匹配），所以种子可能与本次真实列数不同——measure 后会校正。
  // 用共享 computeHomeSeedPageSize（读 raw localStorage），与预取 boot 逐字段同算法，
  // 保证 boot 预热的列表 size 与本 hook 首屏请求的 size 一致（否则 dedupKey 不匹配 → 预取 miss）。
  const [pageSize, setPageSize] = useState(() => {
    if (typeof window === 'undefined' || !storageKey) return fallback
    return computeHomeSeedPageSize(
      window.localStorage.getItem(STORAGE_PREFIX + storageKey)
    )
  })
  const pageSizeRef = useRef<number>()
  if (pageSizeRef.current === undefined) pageSizeRef.current = pageSize
  const pageSizeMeasuredRef = useRef(false)

  const measure = useCallback(() => {
    const el = gridRef.current
    if (!el) return
    const cs = getComputedStyle(el)
    // grid-template-columns 形如 "196.5px 196.5px"，按空格切分：段数=列数，单段=列宽
    const tracks = cs.gridTemplateColumns
      .split(' ')
      .filter((part) => part && part !== 'none')
    const nextCols = tracks.length
    if (nextCols <= 0) return

    const colWidth = parseFloat(tracks[0]) || 0
    const rowGap = parseFloat(cs.rowGap) || 0
    // 单行盒子的真实高度（不含行间距）——这是每个 virtua item（行 div）的实际占高，
    // 用作 itemSize 必须精确等于它，否则 virtua 对未测量行估算偏差会在滚动/加载时
    // 持续修正总高、把视口往上拉（之前多加 rowGap 偏大，导致每行下修 ~12px 上移）。
    const rowBoxHeight = colWidth * itemAspectRatio
    // 行与行的堆叠步距 = 行高 + 行间距，仅用于反推「铺满首屏需要几行」。
    const rowPitch = rowBoxHeight + rowGap
    const viewportH =
      viewportRef?.current?.clientHeight ||
      (typeof window !== 'undefined' ? window.innerHeight : 0)

    const nextRows =
      rowPitch > 0 && viewportH > 0
        ? Math.min(
            maxRows,
            Math.max(minRows, Math.ceil((viewportH * screenBuffer) / rowPitch))
          )
        : minRows
    // nextCols × nextRows 天然是列数整数倍 → 渲染必为整行，无残行
    const nextSize = nextCols * nextRows

    if (!pageSizeMeasuredRef.current) {
      pageSizeMeasuredRef.current = true
      markPerformanceStage(HOME_PERFORMANCE_MARKS.pageSizeMeasured)
    }

    if (nextCols !== columnsRef.current) {
      columnsRef.current = nextCols
      setColumns(nextCols)
    }
    // 行高用整数像素，避免亚像素抖动反复 setState
    const nextRowHeight = Math.round(rowBoxHeight)
    if (nextRowHeight > 0 && nextRowHeight !== rowHeightRef.current) {
      rowHeightRef.current = nextRowHeight
      setRowHeight(nextRowHeight)
    }
    const nextRowGap = Math.round(rowGap)
    if (nextRowGap !== rowGapRef.current) {
      rowGapRef.current = nextRowGap
      setRowGap(nextRowGap)
    }
    // 预取提前量 = 一屏的行数（ceil(视口高 / 行步距)），至少 1 行。
    const nextPrefetchRows =
      rowPitch > 0 && viewportH > 0
        ? Math.max(1, Math.ceil(viewportH / rowPitch))
        : 0
    if (nextPrefetchRows !== prefetchRowsRef.current) {
      prefetchRowsRef.current = nextPrefetchRows
      setPrefetchRows(nextPrefetchRows)
    }
    if (nextSize !== pageSizeRef.current) {
      pageSizeRef.current = nextSize
      setPageSize(nextSize)
      writeStoredGrid(storageKey, { cols: nextCols, rows: nextRows })
    }
  }, [
    gridRef,
    viewportRef,
    itemAspectRatio,
    screenBuffer,
    minRows,
    maxRows,
    storageKey,
  ])

  useEffect(() => {
    const el = gridRef.current
    if (!el || typeof ResizeObserver === 'undefined') {
      measure()
      return
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [gridRef, measure])

  return { columns, pageSize, rowHeight, rowGap, prefetchRows }
}

export default useResponsivePageSize
